import {Button, Card, CardContent, CardHeader, Grid, Box as _Box, Typography, Stack} from "@mui/material";
import React, {FC, useCallback, useState} from "react";
import {useNavigate} from "react-router";
import {Add, Remove, SwitchLeft} from "@mui/icons-material";
import TimberTypeName from "../../timberType/TimberTypeDetail/TimberTypeName.tsx";
import TimberClassName from "../../timberClass/TimberClassDetail/TimberClassName.tsx";
import ShipmentName from "../ShipmentDetail/ShipmentName.tsx";
import {Cordwood} from "../../../types/models/Cordwood.ts";
import {PrimaryShipment, Shipment} from "../../../types/models/Shipment.ts";
import {useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {PrimaryCutting} from "../../../types/models/Cutting.ts";
import {useStateSession} from "../../../../hooks/useAuth.ts";
import {useGetShipmentQuery} from "../../../apis/Shipment.api.ts";
import {cuttingStateEditCheck, shippingStateCheck, shippingStateEditCheck} from "../../../stateChecks.ts";
import TimberStorageName from "../../timberStorage/TimberStorageDetail/TimberStorageName.tsx";
import * as crudgen from "../../../../share";
import {styled} from "@mui/material/styles";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import {
    CordwoodMeasurementType,
    CordwoodMeasurementTypePairs,
    toString as toStringCordwoodMeasurementType
} from "../../../types/models/CordwoodMeasurementType.d";
import {calcVolumeM3} from "../../../../utils/measurementUnits.ts";
import Box from "@mui/material/Box";

const InfoBox = styled(_Box)`
    padding: 0.4rem 0.7rem;
    margin-top: 16px;
`;

type IFormSelectProps = {
    shipment: Shipment,
    data: Cordwood;
    onAddCordwood?: (cordwood: Cordwood, volume: number, measurementType: CordwoodMeasurementType) => void;
    onRemoveCordwood?: (cordwood: Cordwood) => void;
}

const CordwoodDetail: FC<IFormSelectProps> = ({data, onAddCordwood, onRemoveCordwood, shipment}) => {

    const navigate = useNavigate()
    const session = useStateSession()
    
    const [volume, setVolume] = useState(data.volume)
    const [measurementType, setMeasurementType] = useState({
        id: data.measurementType,
        name: data.measurementType,
    })

    const onAddCordwoodCallback = useCallback((e: any, cordwood: Cordwood) => {
        e.stopPropagation()
        onAddCordwood(cordwood, volume, measurementType.id)
    }, [navigate, volume, measurementType])

    const onRemoveCordwoodCallback = useCallback((e: any, cordwood: Cordwood) => {
        e.stopPropagation()
        onRemoveCordwood(cordwood)
    }, [navigate])

    const isInShipment = data.shipmentId === shipment.id
    const isNotInAnyShipment = data.shipmentId == undefined
    const isInOtherShipment = !isInShipment && !isNotInAnyShipment
    const isInOtherStorage = data.storageId !== shipment.storageId

    const { data: cutting } = useGetCuttingQuery(PrimaryCutting.fromId(shipment?.cuttingId), {skip: !shipment?.cuttingId})
    const { data: timberShipment } = useGetShipmentQuery(PrimaryShipment.fromId(data?.shipmentId), {skip: isInShipment || !data?.shipmentId})

    const showEdit = session.hasPermission('', 'applications/timber/entities/Shipments/update/view')
        && cuttingStateEditCheck(cutting?.state)
        && shippingStateEditCheck(shipment?.state)
        && (!timberShipment || shippingStateEditCheck(timberShipment?.state))
        && shippingStateCheck(session, shipment?.state)
    
    const color = isInShipment ? 'red' : isInOtherShipment ? 'orange' : 'blue'

    return (
        <Card variant="outlined">
            <CardHeader title={data.internalKey}></CardHeader>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={6}>
                        <InfoBox>
                            {data.shipmentId && (
                                <Typography variant="body2" textAlign="left" sx={{color: color}}>
                                    <Trans>Shipment</Trans>: <ShipmentName  id={data.shipmentId} />
                                </Typography>
                            )}
                            <Typography variant="body2" textAlign="left">
                                <Trans>Volume</Trans>: {fixedFormat(data.volume)} {data.measurementType}
                            </Typography>
                            <Typography variant="body2" textAlign="left">
                                <Trans>Timber Type</Trans>: <TimberTypeName id={data.timberTypeId} />
                            </Typography>
                            <Typography variant="body2" textAlign="left">
                                <Trans>Class</Trans>: <TimberClassName id={data.classId} />
                            </Typography>
                            <Typography variant="body2" textAlign="left">
                                <Trans>Storage</Trans>: <TimberStorageName id={data.storageId}/>
                            </Typography>
                            {data.description ?
                                <Typography variant="body2" textAlign="left">
                                    <Trans>Description</Trans>: {data.description}
                                </Typography>
                                :
                                <></>
                            }
                        </InfoBox>
                    </Grid>
                    <Grid size={6} sx={{ verticalAlign: 'top' }}>
                        {isInOtherStorage ?
                            <>
                                <Trans>Storage</Trans>: <TimberStorageName id={data.storageId}/>
                            </>
                            :
                            <>
                                {isNotInAnyShipment && onAddCordwood && showEdit && (
                                    <>
                                        <Stack direction="row">
                                            <crudgen.FormInput
                                                required
                                                name={ 'volume' }
                                                label={t`Volume`}
                                                type='number'
                                                checkNumber={true}
                                                forceNumber={true}
                                                value={volume}
                                                onValueChange={(value) => setVolume(value)}
                                            />
                                            <crudgen.FormSelect
                                                required
                                                name={ 'measurementType' }
                                                aria-label="measurementType"
                                                label={t`Measurement Type`}
                                                value={ measurementType }
                                                options={ CordwoodMeasurementTypePairs }
                                                optionLabelMap={ toStringCordwoodMeasurementType }
                                                onValueChange={(value) => setMeasurementType(value)}
                                            />
                                        </Stack>
                                        { measurementType && measurementType?.id != 'm3' &&
                                            <Box component="section" sx={{ paddingBottom: 1, paddingLeft: 1 }}>
                                                <Typography variant="body2">
                                                    ({ fixedFormat(calcVolumeM3(volume, measurementType.id)) } m&#xB3;)
                                                </Typography>
                                            </Box>
                                        }
                                        <Button size="small" variant="outlined" startIcon={<Add/>} onClick={(e) => onAddCordwoodCallback(e, data)}>
                                            <Trans>Add to shipment</Trans>
                                        </Button>
                                    </>
                                )}
                                {isInOtherShipment && onAddCordwood && showEdit && (
                                    <>
                                        <crudgen.FormInput
                                            required
                                            name={ 'volume' }
                                            label={t`Volume (m&#xB3;)`}
                                            type='number'
                                            checkNumber={true}
                                            forceNumber={true}
                                            value={volume}
                                            onValueChange={(value) => setVolume(value)}
                                        />
                                        <Button size="small" variant="outlined" color="warning" startIcon={<SwitchLeft/>}
                                                onClick={(e) => onAddCordwoodCallback(e, data)}>
                                            <Trans>Move to this shipment</Trans>
                                        </Button>
                                    </>
                                )}
                                {isInShipment && onRemoveCordwood && showEdit && (
                                    <Button size="small" variant="outlined" color="error" startIcon={<Remove/>}
                                            onClick={(e) => onRemoveCordwoodCallback(e, data)}>
                                        <Trans>Remove from this shipment</Trans>
                                    </Button>
                                )}
                            </>
                        }
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
export default CordwoodDetail;
